// Painel lateral direito (desktop / drawer intermediario).
//
//  - Desktop (>= 1440): renderizado fixo na lateral (360px).
//  - Intermediario (1200-1439): renderizado dentro de Sheet via toggle no Topbar.
//  - <1200: ocultado.
//
// Conteudo REAL (Phase 12 entregue): atividade recente (ultimas transacoes) +
// notificacoes recentes. Sem placeholders "em breve". O mobile nao tem
// right-pane; aqui adaptamos para o desktop reaproveitando as queries que ja
// alimentam home e a tela de notificacoes.

import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Activity, ArrowRight, Bell } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useNotificationsQuery } from '@/features/notifications/api/notifications.queries';
import { useTransactionsQuery } from '@/features/transactions/api/transactions.queries';
import { TransactionItem } from '@/features/transactions/components/transaction-item';
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';

export interface RightPaneProps {
  /** Quando true, renderiza apenas o conteudo (sem coluna fixa) — uso em drawer. */
  embedded?: boolean;
  testID?: string;
}

export function RightPane({ embedded, testID = 'app-right-pane' }: RightPaneProps) {
  return (
    <aside
      data-testid={testID}
      aria-label="Atividade e notificações"
      className={cn(
        'flex flex-col gap-4 overflow-y-auto bg-surface-low px-4 py-4',
        embedded ? 'h-full w-full' : 'sticky top-0 h-screen w-[360px] flex-shrink-0',
      )}
    >
      <RecentActivityCard testID={testID} />
      <RecentNotificationsCard testID={testID} />
    </aside>
  );
}

function RecentActivityCard({ testID }: { testID: string }) {
  const query = useTransactionsQuery({ per_page: 5, sort_order: 'desc' });
  const items = query.data?.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Atividade recente</CardTitle>
        <Activity size={18} aria-hidden className="text-secondary-text" />
      </CardHeader>
      <CardBody>
        {query.isPending ? (
          <ul className="space-y-2" data-testid={`${testID}-activity-skeleton`}>
            {Array.from({ length: 4 }).map((_, i) => (
              <li key={i}>
                <Skeleton height={56} className="w-full" />
              </li>
            ))}
          </ul>
        ) : items.length === 0 ? (
          <p className="text-body-md text-secondary-text" data-testid={`${testID}-activity-empty`}>
            Seus lançamentos mais recentes aparecem aqui.
          </p>
        ) : (
          <>
            <ul className="space-y-1" data-testid={`${testID}-activity-list`}>
              {items.map((tx) => (
                <li key={`${tx.type}-${tx.id}`}>
                  <TransactionItem tx={tx} testID={`${testID}-activity-${tx.id}`} />
                </li>
              ))}
            </ul>
            <Link
              to="/reports/history"
              data-testid={`${testID}-activity-link`}
              className="mt-3 inline-flex items-center gap-1 text-label-md font-bold text-primary hover:underline"
            >
              Ver histórico
              <ArrowRight size={14} aria-hidden />
            </Link>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function RecentNotificationsCard({ testID }: { testID: string }) {
  const query = useNotificationsQuery({ page: 1, per_page: 4 });
  const items = query.data?.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notificações</CardTitle>
        <Bell size={18} aria-hidden className="text-secondary-text" />
      </CardHeader>
      <CardBody>
        {query.isPending ? (
          <ul className="space-y-2" data-testid={`${testID}-notif-skeleton`}>
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i}>
                <Skeleton height={48} className="w-full" />
              </li>
            ))}
          </ul>
        ) : items.length === 0 ? (
          <p className="text-body-md text-secondary-text" data-testid={`${testID}-notif-empty`}>
            Sem notificações por enquanto.
          </p>
        ) : (
          <>
            <ul className="space-y-3" data-testid={`${testID}-notif-list`}>
              {items.map((n) => {
                const unread = n.readAt === null;
                return (
                  <li
                    key={n.id}
                    className="flex items-start gap-3"
                    data-testid={`${testID}-notif-${n.id}`}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'mt-1.5 inline-flex h-2 w-2 flex-shrink-0 rounded-pill',
                        unread ? 'bg-primary' : 'bg-surface-highest',
                      )}
                    />
                    <div className="min-w-0">
                      <p className="truncate text-body-md text-on-surface">{n.title}</p>
                      <p className="line-clamp-2 text-label-md text-secondary-text">{n.message}</p>
                      <p className="mt-0.5 text-label-sm text-secondary-text">
                        {formatDistanceToNow(new Date(n.createdAt), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
            <Link
              to="/notifications"
              data-testid={`${testID}-notif-link`}
              className="mt-3 inline-flex items-center gap-1 text-label-md font-bold text-primary hover:underline"
            >
              Ver todas
              <ArrowRight size={14} aria-hidden />
            </Link>
          </>
        )}
      </CardBody>
    </Card>
  );
}
